import React, { useState, useEffect } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KYCForm = ({ isOpen, onClose, selectedPlan }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    trading_experience: '',
    capital_amount: '',
    plan_selected: selectedPlan || '',
    additional_info: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [kycId, setKycId] = useState('');

  // Sync plan_selected when selectedPlan prop changes or modal opens
  useEffect(() => {
    if (isOpen && selectedPlan) {
      setFormData(prev => ({
        ...prev,
        plan_selected: selectedPlan
      }));
    }
  }, [selectedPlan, isOpen]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/kyc`, formData);
      setKycId(response.data.id);
      setIsSuccess(true);
      
      // Redirect to WhatsApp after 2 seconds
      setTimeout(() => {
        const message = `Hello! I've completed the KYC form for ${formData.plan_selected} plan. My KYC ID is: ${response.data.id}`;
        const whatsappUrl = `https://wa.me/971554276609?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit KYC. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = `Hello! I've completed the KYC form for ${formData.plan_selected} plan. My KYC ID is: ${kycId}`;
    const whatsappUrl = `https://wa.me/971554276609?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="relative bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>

        {!isSuccess ? (
          <div className="p-8">
            <h2 className="text-3xl font-bold text-white mb-2">Complete Your KYC</h2>
            <p className="text-slate-400 mb-8">Fill in your details to get started with {selectedPlan} plan</p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Full Name */}
              <div>
                <Label htmlFor="full_name" className="text-white mb-2 block">Full Name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={handleChange}
                  className="bg-slate-800/50 border-slate-700 text-white"
                  placeholder="John Doe"
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-white mb-2 block">Email Address *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="bg-slate-800/50 border-slate-700 text-white"
                  placeholder="john@example.com"
                />
              </div>

              {/* Phone */}
              <div>
                <Label htmlFor="phone" className="text-white mb-2 block">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-slate-800/50 border-slate-700 text-white"
                  placeholder="+971 50 123 4567"
                />
              </div>

              {/* Country */}
              <div>
                <Label htmlFor="country" className="text-white mb-2 block">Country *</Label>
                <Input
                  id="country"
                  name="country"
                  type="text"
                  required
                  value={formData.country}
                  onChange={handleChange}
                  className="bg-slate-800/50 border-slate-700 text-white"
                  placeholder="United Arab Emirates"
                />
              </div>

              {/* Trading Experience */}
              <div>
                <Label htmlFor="trading_experience" className="text-white mb-2 block">Trading Experience *</Label>
                <Select
                  value={formData.trading_experience}
                  onValueChange={(value) => handleSelectChange('trading_experience', value)}
                  required
                >
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                    <SelectValue placeholder="Select your experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Beginner">Beginner (0-6 months)</SelectItem>
                    <SelectItem value="Intermediate">Intermediate (6 months - 2 years)</SelectItem>
                    <SelectItem value="Advanced">Advanced (2-5 years)</SelectItem>
                    <SelectItem value="Expert">Expert (5+ years)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Capital Amount */}
              <div>
                <Label htmlFor="capital_amount" className="text-white mb-2 block">Trading Capital *</Label>
                <Select
                  value={formData.capital_amount}
                  onValueChange={(value) => handleSelectChange('capital_amount', value)}
                  required
                >
                  <SelectTrigger className="bg-slate-800/50 border-slate-700 text-white">
                    <SelectValue placeholder="Select capital range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="$1,000 - $5,000">$1,000 - $5,000</SelectItem>
                    <SelectItem value="$5,000 - $10,000">$5,000 - $10,000</SelectItem>
                    <SelectItem value="$10,000 - $25,000">$10,000 - $25,000</SelectItem>
                    <SelectItem value="$25,000 - $50,000">$25,000 - $50,000</SelectItem>
                    <SelectItem value="$50,000+">$50,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Additional Info */}
              <div>
                <Label htmlFor="additional_info" className="text-white mb-2 block">Additional Information (Optional)</Label>
                <Textarea
                  id="additional_info"
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleChange}
                  className="bg-slate-800/50 border-slate-700 text-white min-h-[100px]"
                  placeholder="Any questions or special requirements..."
                />
              </div>

              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 text-red-400 text-sm">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white py-6 text-lg font-bold"
              >
                {isSubmitting ? 'Submitting...' : 'Submit KYC Application'}
              </Button>
            </form>
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="w-20 h-20 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">KYC Submitted Successfully!</h2>
            <p className="text-slate-400 mb-2">Your KYC ID: <span className="text-cyan-400 font-mono">{kycId}</span></p>
            <p className="text-slate-300 mb-8">
              Our team will review your application and contact you shortly via WhatsApp.
            </p>
            <Button
              onClick={handleWhatsAppClick}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-8 py-4 text-lg font-bold"
            >
              Continue to WhatsApp
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default KYCForm;
